import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI } from '../services/api';
import { Eye, EyeOff } from 'lucide-react';

function Login({ setAuth, setUser }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await authAPI.login({ email, password });
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      setAuth(true);
      if (setUser) setUser(res.data.user);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'white',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      paddingBottom: '80px',
      fontFamily: 'var(--font-body)',
    }}>

      {/* Logo */}
      <div style={{ marginBottom: 28, textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center' }}>
          <svg width="20" height="24" viewBox="-1 -1 34 40" xmlns="http://www.w3.org/2000/svg">
            <path d="M16 1 L31 6 L31 21 Q31 33 16 37 Q1 33 1 21 L1 6 Z" fill="white" stroke="#0A1628" strokeWidth="2"/>
            <path d="M6 13 Q11 9 16 12 Q21 15 26 11" stroke="#0A1628" strokeWidth="1.8" fill="none" strokeLinecap="round"/>
            <path d="M6 18 Q11 14 16 17 Q21 20 26 16" stroke="#0066FF" strokeWidth="1.8" fill="none" strokeLinecap="round"/>
            <path d="M6 23 Q11 19 16 22 Q21 25 26 21" stroke="#0066FF" strokeWidth="1.4" fill="none" strokeLinecap="round" opacity="0.6"/>
            <path d="M7 28 Q11 24 16 27 Q21 30 25 26" stroke="#0066FF" strokeWidth="1.1" fill="none" strokeLinecap="round" opacity="0.3"/>
          </svg>
          <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 26, fontWeight: 800, letterSpacing: '-1px' }}>
            <span style={{ color: '#0066FF' }}>Air</span><span style={{ color: '#0A1628' }}>Comply</span>
          </div>
        </div>
      </div>

      {/* Card */}
      <div style={{
        width: '100%',
        maxWidth: 400,
        background: 'white',
        border: '1px solid #E2E8F0',
        borderRadius: 16,
        padding: '36px 32px',
      }}>
        <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 20, fontWeight: 700, color: '#0A1628', margin: '0 0 28px', textAlign: 'center', letterSpacing: '-0.3px' }}>
          Sign in to account
        </h1>

        {error && (
          <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '10px 14px', marginBottom: 18, fontSize: 13, color: '#DC2626' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Email */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
              Email
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder=""
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: 8,
                fontSize: 14,
                fontFamily: 'var(--font-body)',
                color: '#0A1628',
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'border-color 0.15s',
                background: 'white',
              }}
              onFocus={e => e.target.style.borderColor = '#0066FF'}
              onBlur={e => e.target.style.borderColor = '#D1D5DB'}
            />
          </div>

          {/* Password */}
          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
              Password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                required
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder=""
                style={{
                  width: '100%',
                  padding: '10px 40px 10px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: 8,
                  fontSize: 14,
                  fontFamily: 'var(--font-body)',
                  color: '#0A1628',
                  outline: 'none',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.15s',
                  background: 'white',
                }}
                onFocus={e => e.target.style.borderColor = '#0066FF'}
                onBlur={e => e.target.style.borderColor = '#D1D5DB'}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#9CA3AF',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </div>
          </div>

          {/* Sign in button */}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '11px',
              background: loading ? '#94a3b8' : '#0066FF',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              fontFamily: 'var(--font-body)',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'background 0.15s',
              marginBottom: 10,
            }}
          >
            {loading ? 'Signing in...' : 'Sign in'}
          </button>

          {/* Terms acceptance */}
          <p style={{ fontSize: 11, color: '#9CA3AF', textAlign: 'center', margin: '0 0 16px', lineHeight: 1.5 }}>
            By signing in you agree to our{' '}
            <a href="https://aircomply.com/terms.html" target="_blank" rel="noopener noreferrer"
              style={{ color: '#9CA3AF', textDecoration: 'none', borderBottom: '1px solid #D1D5DB' }}>Terms</a>
            {' '}&amp;{' '}
            <a href="https://aircomply.com/privacy.html" target="_blank" rel="noopener noreferrer"
              style={{ color: '#9CA3AF', textDecoration: 'none', borderBottom: '1px solid #D1D5DB' }}>Privacy Policy</a>
          </p>

          {/* Forgot password */}
          <div style={{ textAlign: 'center' }}>
            <span style={{ fontSize: 13, color: '#6B7280' }}>Having trouble? </span>
            <Link to="/forgot-password" style={{ fontSize: 13, color: '#0066FF', fontWeight: 500, textDecoration: 'none' }}>
              Reset password
            </Link>
          </div>
        </form>
      </div>

      {/* Access note */}
      <div style={{ marginTop: 20, textAlign: 'center', fontSize: 13, color: '#94a3b8' }}>
        Access is by invitation only ·{' '}
        <a href="mailto:sales@aircomply.com" style={{ color: '#6B7280', textDecoration: 'none', fontWeight: 500 }}>
          sales@aircomply.com
        </a>
      </div>


    </div>
  );
}

export default Login;
